import React from "react";
import { useCreative } from "src/contexts/CreativeContext";
import SortDropdown from "src/components/common/dropdown/SortDropdown";
import TimelineDropdown from "src/components/common/dropdown/TimelineDropdown";
import CreativesDetailModal from "src/components/modal/CreativesDetailModal";
import { sortOptions } from "src/utils/sortOptions";
import Pagination from "src/components/pages/your-campaign/Pagination";

export default function YourCreatives() {
  const {
    creatives,
    isLoading,
    error,
    fetchCreatives,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    seeAll,
    setSeeAll,
    totalCount
  } = useCreative();

  const [openModal, setOpenModal] = React.useState(false);
  const [selectedCreative, setSelectedCreative] = React.useState<any>(null);

  // React.useEffect(() => {
  //   fetchCreatives();
  // }, []);

  const openDetails = (creative: any) => {
    setSelectedCreative(creative);
    setOpenModal(true);
  };

  const handleSortChange = (selected: any) => {
    const sortedCreatives = [...creatives];

    switch (selected.value) {
      case 'leads':
        sortedCreatives.sort((a, b) =>
          parseInt(b.metrics.leads.replace(/,/g, '')) - parseInt(a.metrics.leads.replace(/,/g, ''))
        );
        break;
      case 'clicks':
        sortedCreatives.sort((a, b) =>
          parseInt(b.metrics.clicks.replace(/,/g, '')) - parseInt(a.metrics.clicks.replace(/,/g, ''))
        );
        break;
      case 'spend':
        sortedCreatives.sort((a, b) =>
          parseFloat(b.metrics.spend.replace('$', '').replace(/,/g, '')) -
          parseFloat(a.metrics.spend.replace('$', '').replace(/,/g, ''))
        );
        break;
      case 'hookRate':
        sortedCreatives.sort((a, b) =>
          parseFloat(b.metrics.hookRate.replace('%', '')) -
          parseFloat(a.metrics.hookRate.replace('%', ''))
        );
        break;
      default:
        sortedCreatives.sort((a, b) =>
          parseFloat(b.metrics.spend.replace('$', '').replace(/,/g, '')) -
          parseFloat(a.metrics.spend.replace('$', '').replace(/,/g, ''))
        );
    }

    // Note: In a real app, you might want to store sorted creatives in state
    // For now, we'll just log the sorted array
    console.log('Sorted creatives:', sortedCreatives);
  };

  const handleTimelineChange = (selected: any) => {
    console.log("Selected Timeline:", selected);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSeeAllToggle = () => {
    const newSeeAll = !seeAll;
    setSeeAll(newSeeAll);
    fetchCreatives(newSeeAll);
  };

  // Calculate pagination values
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = creatives.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 animate-pulse">
      <div className="w-full h-48 bg-gray-200 rounded-lg mb-4"></div>
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
      <div className="mt-3 h-4 bg-gray-200 rounded w-1/3"></div>
    </div>
  );

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex justify-between items-center pb-6">
        <div>
          <p className="text-[32px] font-semibold text-black font-inter">Your Creatives</p>
          <p className="text-lg font-inter font-normal text-[#4A4D4F]">
            Here are all the creatives across all ad accounts.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <TimelineDropdown onChange={handleTimelineChange} />
          <SortDropdown options={sortOptions} onChange={handleSortChange} />
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={seeAll}
                onChange={handleSeeAllToggle}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">Show All</span>
            </label>
          </div>
          <button className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition">
            + Add new creative
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Ad Accounts Summary */}
      {!isLoading && creatives.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700">
            Showing creatives from {new Set(creatives.map(c => c.adAccountId)).size} ad accounts
          </p>
        </div>
      )}

      {/* Creatives Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {isLoading ? (
          Array.from({ length: itemsPerPage }).map((_, index) => (
            <SkeletonCard key={index} />
          ))
        ) : (
          currentItems.map((item, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
              {/* Ad Account Badge */}
              <div className="mb-2">
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  {item.adAccountName}
                </span>
              </div>

              {/* Video/Image Thumbnail */}
              <div className="relative w-full h-48 rounded-lg overflow-hidden mb-4">
                <img
                  src={item.thumbnail}
                  alt="Creative Thumbnail"
                  className="w-full h-full object-cover"
                />
                {item.isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <button className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md">
                      ▶
                    </button>
                  </div>
                )}
              </div>

              {/* Metrics */}
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex justify-between"><span>Leads</span><span className="font-semibold">{item.metrics.leads}</span></div>
                <div className="flex justify-between"><span>Clicks</span><span className="font-semibold">{item.metrics.clicks}</span></div>
                <div className="flex justify-between"><span>Cost/Lead</span><span className="font-semibold">{item.metrics.costPerLead}</span></div>
                <div className="flex justify-between"><span>Cost/Click</span><span className="font-semibold">{item.metrics.costPerClick}</span></div>
                <div className="flex justify-between"><span>Hook Rate</span><span className="font-semibold">{item.metrics.hookRate}</span></div>
                <div className="flex justify-between"><span>Spend</span><span className="font-semibold">{item.metrics.spend}</span></div>
              </div>

              {/* View Details */}
              <div className="mt-3">
                <button
                  onClick={() => openDetails(item)}
                  className="text-blue-600 text-sm font-medium hover:underline"
                >
                  View details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!isLoading && creatives.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={creatives.length}
          pageSize={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Empty state */}
      {!isLoading && creatives.length === 0 && !error && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">No creatives found across your ad accounts</div>
          <button
            onClick={() => fetchCreatives()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Modal Component */}
      <CreativesDetailModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        creative={selectedCreative}
      />
    </div>
  );
}